/**
 * FIE Page — Tabbed container for all Financial Intelligence Engine screens
 */
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { BarChart3, Users, Settings2, GitCompare, Shield } from "lucide-react";
import { useRole } from "@/data/RoleContext";
import { getFIEPermissions, hasFIEAccess } from "@/data/fie/permissions";
import { FIEProvider } from "@/data/fie/context";
import { EarningsForecastDashboard } from "@/components/fie/EarningsForecastDashboard";
import { PartnerDistributionsView } from "@/components/fie/PartnerDistributionsView";
import { AssumptionsRegistryView } from "@/components/fie/AssumptionsRegistryView";
import { ReconciliationView } from "@/components/fie/ReconciliationView";
import { FIEAuditLogView } from "@/components/fie/FIEAuditLogView";
import { RoleContextBanner } from "@/components/RoleContextBanner";

export default function EarningsIntelligencePage() {
  const { role } = useRole();
  const perms = getFIEPermissions(role);
  const hasAccess = hasFIEAccess(role);

  if (!hasAccess || !perms) {
    return (
      <div className="flex items-center justify-center min-h-[60vh]">
        <div className="text-center space-y-3">
          <Shield className="h-12 w-12 text-muted-foreground mx-auto" />
          <h2 className="text-xl font-bold">Restricted Access</h2>
          <p className="text-sm text-muted-foreground max-w-md">
            The Financial Intelligence Engine is restricted to authorized finance and executive roles.
          </p>
        </div>
      </div>
    );
  }

  return (
    <FIEProvider>
      <div className="space-y-5 p-6 max-w-[1400px] mx-auto">
        <RoleContextBanner />

        <Tabs defaultValue="forecast" className="space-y-5">
          <div className="flex items-center justify-between">
            <TabsList className="bg-muted/50">
              <TabsTrigger value="forecast" className="gap-1.5 text-[13px]">
                <BarChart3 className="h-3.5 w-3.5" /> Forecast
              </TabsTrigger>
              {(perms.viewAllPartnerProjections || perms.viewOwnProjectionOnly) && (
                <TabsTrigger value="distributions" className="gap-1.5 text-[13px]">
                  <Users className="h-3.5 w-3.5" /> Distributions
                </TabsTrigger>
              )}
              {(perms.editAssumptions || perms.viewCostBreakdown) && (
                <TabsTrigger value="assumptions" className="gap-1.5 text-[13px]">
                  <Settings2 className="h-3.5 w-3.5" /> Assumptions
                </TabsTrigger>
              )}
              {perms.viewReconciliation && (
                <TabsTrigger value="reconciliation" className="gap-1.5 text-[13px]">
                  <GitCompare className="h-3.5 w-3.5" /> Reconciliation
                </TabsTrigger>
              )}
              {perms.viewAuditLog && (
                <TabsTrigger value="audit" className="gap-1.5 text-[13px]">
                  <Shield className="h-3.5 w-3.5" /> Audit Log
                </TabsTrigger>
              )}
            </TabsList>
          </div>

          <TabsContent value="forecast"><EarningsForecastDashboard /></TabsContent>
          <TabsContent value="distributions"><PartnerDistributionsView /></TabsContent>
          <TabsContent value="assumptions"><AssumptionsRegistryView /></TabsContent>
          <TabsContent value="reconciliation"><ReconciliationView /></TabsContent>
          <TabsContent value="audit"><FIEAuditLogView /></TabsContent>
        </Tabs>
      </div>
    </FIEProvider>
  );
}
